import React, { useEffect, useState } from "react"
import {
    Alert,
    Card,
    CardBody,
    Col,
    Form,
    FormGroup,
    Row,
} from "reactstrap"
import { useFormik } from "formik"
import { useDispatch, useSelector } from "react-redux"
import * as Yup from "yup"
import { validateBeforeConfirm } from "helpers/validation_helper"
import { toast } from "sonner";
import * as url from "../../helpers/url_helper"
import {
    getMerchantTimings,
    manageMerchantTiming,
    addTimingToForm,
    updateTimingInForm,
    removeTimingFromForm,
    clearTimingForm,
    clearMerchantTimings,
} from "store/merchants/actions"
import TextInput from "components/Common/TextInput"
import SelectInput from "components/Common/SelectInput"
import CheckboxInput from "components/Common/CheckboxInput"
import ConfirmButton from "components/Common/ConfirmButton"
import CButton from "components/Common/CButton"
import IconButton from "components/Common/IconButton"
import SimpleTableContainer from "components/Common/SimpleTableContainer"
import AccessControlWrapper from "components/Common/PermissionButton"

export default function MerchantTimingsForm({
    page,
    match,
    history,
    onTabCompletion,
    activeTab,
    tabId,
    onFormRef,
    createdMerchantID,
}) {
    const [showAddForm, setShowAddForm] = useState(page === "add")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [editingTimingIndex, setEditingTimingIndex] = useState(null)
    const [isEditMode, setIsEditMode] = useState(false)
    const [previousTimingsLength, setPreviousTimingsLength] = useState(0)
    const [currentTiming, setCurrentTiming] = useState({
        weekday: editingTimingIndex !== null ? "" : [],  // Array for new items, string for edit
        startTime: "",
        endTime: "",
        isFullDay: false,
        isMidnight: false,
    })

    const dispatch = useDispatch()
    const detailID = match?.params?.id
    const merchantID = page === "add" ? createdMerchantID : detailID

    // Weekday options (backend accepts max 3 characters)
    const weekdayOptions = [
        { label: "Monday", value: "Mon" },
        { label: "Tuesday", value: "Tue" },
        { label: "Wednesday", value: "Wed" },
        { label: "Thursday", value: "Thu" },
        { label: "Friday", value: "Fri" },
        { label: "Saturday", value: "Sat" },
        { label: "Sunday", value: "Sun" },
    ]

    // Helper function to convert time string to minutes for comparison
    const timeToMinutes = (timeString) => {
        if (!timeString) return 0
        const [hours, minutes] = timeString.split(':').map(Number)
        return hours * 60 + minutes
    }

    // Helper function to check if two time ranges overlap
    const doTimesOverlap = (start1, end1, start2, end2) => {
        const start1Minutes = timeToMinutes(start1)
        const end1Minutes = timeToMinutes(end1)
        const start2Minutes = timeToMinutes(start2)
        const end2Minutes = timeToMinutes(end2)

        return start1Minutes < end2Minutes && start2Minutes < end1Minutes
    }

    // Helper function to get existing time ranges for a weekday
    const getExistingTimeRanges = (weekday, excludeIndex = null) => {
        return timingFormData
            .filter((timing, index) => timing.weekday === weekday && index !== excludeIndex)
            .map(timing => ({
                start: timing.startTime,
                end: timing.endTime
            }))
    }

    // Helper function to convert HH:mm:ss to display format (keep original format)
    const formatTimeForDisplay = (timeString) => {
        if (!timeString) return ""

        const timeStr = String(timeString).trim()

        return timeStr
    }

    const formatTimeForPayload = (timeString) => {
        if (!timeString || typeof timeString !== 'string') return ""

        const timeStr = timeString.trim()

        if (timeStr.length === 8 && timeStr.match(/^\d{2}:\d{2}:\d{2}$/)) {
            return timeStr
        }

        if (timeStr.length === 5 && timeStr.match(/^\d{2}:\d{2}$/)) {
            return `${timeStr}:00`
        }

        return timeStr
    }

    const getWeekdayLabel = (value) => {
        const option = weekdayOptions.find(opt => opt.value === value)
        return option ? option.label : value
    }

    const isFullDayTime = (startTime, endTime) => {
        const formattedStart = formatTimeForPayload(startTime)
        const formattedEnd = formatTimeForPayload(endTime)
        return formattedStart === "00:00:00" && formattedEnd === "23:59:59"
    }

    const isMidnightTime = (endTime) => {
        const formattedEnd = formatTimeForPayload(endTime)
        return formattedEnd === "23:59:59"
    }

    const detectCheckboxStates = (startTime, endTime) => {
        const isFullDay = isFullDayTime(startTime, endTime)
        const isMidnight = isMidnightTime(endTime)

        return { isFullDay, isMidnight }
    }

    // Reset form to initial state
    const resetFormState = () => {
        const initialState = {
            weekday: editingTimingIndex !== null ? "" : [],  // Array for new items, string for edit
            startTime: "",
            endTime: "",
            isFullDay: false,
            isMidnight: false,
        }
        setCurrentTiming(initialState)
        validation.resetForm({
            values: {
                weekday: editingTimingIndex !== null ? null : [],  // null for single-select, array for multi-select
                startTime: "",
                endTime: "",
                isFullDay: false,
                isMidnight: false,
            }
        })
    }

    // Validation schema
    const validationSchema = Yup.object({
        weekday: Yup.mixed()
            .test('weekday-required', 'Please select at least one weekday', function (value) {
                // For multi-select (new items)
                if (Array.isArray(value)) {
                    return value.length > 0
                }
                // For single-select (edit mode)
                return value !== null && value !== undefined && value !== ""
            }),
        startTime: Yup.string()
            .matches(/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/, "Please enter time in HH:mm or HH:mm:ss format (e.g., 09:00 or 09:00:30)")
            .required("Please enter start time"),
        endTime: Yup.string()
            .matches(/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/, "Please enter time in HH:mm or HH:mm:ss format (e.g., 17:00 or 17:00:30)")
            .required("Please enter end time")
            .test('end-time-after-start', 'End time must be after start time', function (value) {
                const startTime = this.parent.startTime
                const isFullDay = this.parent.isFullDay
                const isMidnight = this.parent.isMidnight

                // Skip validation for special cases
                if (isFullDay || isMidnight) {
                    return true
                }

                if (startTime && value) {
                    // Convert both to comparable format for validation
                    const startFormatted = formatTimeForPayload(startTime)
                    const endFormatted = formatTimeForPayload(value)
                    return endFormatted > startFormatted
                }
                return true
            })
            .test('no-time-overlap', 'Time slot overlaps with existing timing for this weekday', function (value) {
                const startTime = this.parent.startTime
                const weekday = this.parent.weekday
                const isFullDay = this.parent.isFullDay
                const isMidnight = this.parent.isMidnight

                // Skip validation for special cases or if required fields are missing
                if (isFullDay || isMidnight || !startTime || !value) {
                    return true
                }

                // If no weekday selected, skip overlap check (will be caught by required validation)
                if (!weekday || (Array.isArray(weekday) && weekday.length === 0)) {
                    return true
                }

                // Handle both single and multi-select weekdays
                let weekdaysToCheck = []
                if (Array.isArray(weekday)) {
                    weekdaysToCheck = weekday.filter(day => day && day.value).map(day => day.value)
                } else if (weekday && weekday.value) {
                    weekdaysToCheck = [weekday.value]
                } else {
                    return true // Skip if weekday format is unexpected
                }

                if (weekdaysToCheck.length === 0) {
                    return true
                }

                const newStartTime = formatTimeForPayload(startTime)
                const newEndTime = formatTimeForPayload(value)

                // Check overlap for all selected weekdays
                for (const selectedWeekday of weekdaysToCheck) {
                    const existingRanges = getExistingTimeRanges(selectedWeekday, editingTimingIndex)

                    for (const range of existingRanges) {
                        if (doTimesOverlap(newStartTime, newEndTime, range.start, range.end)) {
                            return this.createError({
                                message: `Time slot overlaps with existing timing for ${getWeekdayLabel(selectedWeekday)}`
                            })
                        }
                    }
                }

                return true
            }),
        isFullDay: Yup.boolean(),
        isMidnight: Yup.boolean(),
    })

    // Form validation using Formik
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            weekday: editingTimingIndex !== null
                ? (currentTiming.weekday ? weekdayOptions.find(opt => opt.value === currentTiming.weekday) : null)
                : (Array.isArray(currentTiming.weekday) ? currentTiming.weekday : []),
            startTime: formatTimeForDisplay(currentTiming.startTime),
            endTime: formatTimeForDisplay(currentTiming.endTime),
            isFullDay: !!currentTiming.isFullDay,
            isMidnight: !!currentTiming.isMidnight,
        },
        validationSchema: validationSchema,
        onSubmit: async () => {
            const isValid = await validateBeforeConfirm(validation)
            if (isValid) {
                handleAddTimingToForm()
            }
        },
    })

    const {
        merchantTimings,
        timingError,
        isLoadingTimings,
        timingFormData,
        timingCreationSuccess,
    } = useSelector(state => ({
        merchantTimings: state.merchants.merchantTimings || [],
        timingError: state.merchants.timingError,
        isLoadingTimings: state.merchants.isLoadingTimings,
        timingFormData: state.merchants.timingFormData || [],
        timingCreationSuccess: state.merchants.timingCreationSuccess,
    }))

    // Fetch timings only when tab is active (check activeTab matches tabId)
    useEffect(() => {
        const isTabActive = activeTab === tabId

        if (page === "detail" && detailID && isTabActive) {
            const options = {
                headers: {
                    "input-parameters": { merchantID: detailID },
                },
            }
            dispatch(getMerchantTimings(options, history))
        } else if (page === "add") {
            dispatch(clearMerchantTimings())
        }
    }, [dispatch, detailID, history, page, activeTab, tabId])

    useEffect(() => {
        dispatch(clearTimingForm())
    }, [dispatch])

    const shouldDisplayError = error => {
        if (!error) return false
        const errorMessage = error.message || error.description || ""
        const noDataPatterns = ["no content available"]
        return !noDataPatterns.some(pattern =>
            errorMessage.toLowerCase().includes(pattern)
        )
    }

    const handleAddTimingToForm = async () => {
        // Validate form before proceeding
        const isValid = await validateBeforeConfirm(validation)
        if (!isValid) {
            return
        }

        const finalStartTime = formatTimeForPayload(validation.values.startTime)
        const finalEndTime = formatTimeForPayload(validation.values.endTime)

        // Handle multi-select (new items) vs single-select (edit mode)
        if (editingTimingIndex !== null) {
            // Edit mode - single weekday
            if (!validation.values.weekday || !validation.values.weekday.value) {
                toast.error("Please select a weekday")
                return
            }

            const selectedWeekday = validation.values.weekday.value
            const duplicateIndex = timingFormData.findIndex((timing, index) =>
                timing.weekday === selectedWeekday && index !== editingTimingIndex
            )

            if (duplicateIndex !== -1) {
                const existingRanges = getExistingTimeRanges(selectedWeekday, editingTimingIndex)
                const hasTimeOverlap = existingRanges.some(range =>
                    doTimesOverlap(finalStartTime, finalEndTime, range.start, range.end)
                )

                if (hasTimeOverlap) {
                    toast.error(`The selected time slot (${validation.values.startTime} - ${validation.values.endTime}) overlaps with an existing timing for ${getWeekdayLabel(selectedWeekday)}.`)
                    return
                }
            }

            const newTiming = {
                weekday: selectedWeekday,
                startTime: finalStartTime,
                endTime: finalEndTime,
                isFullDay: validation.values.isFullDay,
                isMidnight: validation.values.isMidnight,
            }

            dispatch(updateTimingInForm(editingTimingIndex, newTiming))
            setEditingTimingIndex(null)
        } else {
            // Add mode - multiple weekdays
            const selectedWeekdays = Array.isArray(validation.values.weekday)
                ? validation.values.weekday
                : [validation.values.weekday]

            // Validate we have weekdays selected
            if (!selectedWeekdays || selectedWeekdays.length === 0) {
                toast.error("Please select at least one weekday")
                return
            }

            // Filter out any null/undefined values and ensure we have value property
            const validWeekdays = selectedWeekdays.filter(w => w && w.value)

            if (validWeekdays.length === 0) {
                toast.error("Please select at least one weekday")
                return
            }

            // Check for overlaps for each selected weekday
            for (const weekdayOption of validWeekdays) {
                const selectedWeekday = weekdayOption.value
                const existingRanges = getExistingTimeRanges(selectedWeekday, null)

                const hasTimeOverlap = existingRanges.some(range =>
                    doTimesOverlap(finalStartTime, finalEndTime, range.start, range.end)
                )

                if (hasTimeOverlap) {
                    toast.error(`The selected time slot (${validation.values.startTime} - ${validation.values.endTime}) overlaps with an existing timing for ${getWeekdayLabel(selectedWeekday)}.`)
                    return
                }
            }

            // Add timing for each selected weekday
            for (const weekdayOption of validWeekdays) {
                const newTiming = {
                    weekday: weekdayOption.value,
                    startTime: finalStartTime,
                    endTime: finalEndTime,
                    isFullDay: validation.values.isFullDay,
                    isMidnight: validation.values.isMidnight,
                }
                dispatch(addTimingToForm(newTiming))
            }

            // Show success message
            const weekdayNames = validWeekdays.map(w => getWeekdayLabel(w.value)).join(", ")
            toast.success(`Added timing for ${weekdayNames}`)
        }

        // Reset form completely after adding/updating
        resetFormState()
    }

    const handleEditTiming = (timing, index) => {
        setCurrentTiming({
            weekday: timing.weekday,
            startTime: timing.startTime,
            endTime: timing.endTime,
            isFullDay: !!timing.isFullDay,
            isMidnight: !!timing.isMidnight,
        })
        setEditingTimingIndex(index)
    }

    const handleDeleteTimingFromForm = (index) => {
        dispatch(removeTimingFromForm(index))

        if (editingTimingIndex === index) {
            setEditingTimingIndex(null)
            resetFormState()
        }
    }

    const handleCancelEdit = () => {
        setEditingTimingIndex(null)
        resetFormState()
    }

    const handleFinalSubmit = () => {
        // Validate we have timings to submit
        if (!timingFormData || timingFormData.length === 0) {
            toast.error("Please add at least one timing before saving")
            return
        }

        setIsSubmitting(true)

        const timingData = timingFormData.map(timing => ({
            weekday: timing.weekday,
            startTime: timing.startTime,
            endTime: timing.endTime,
        }))

        const payload = {
            merchantID: parseInt(merchantID),
            timings: timingData
        }

        const options = {
            headers: {
                "input-parameters": payload,
            },
        }

        dispatch(manageMerchantTiming(payload, options, history))
    }


    useEffect(() => {
        if (timingCreationSuccess && isSubmitting) {
            setIsSubmitting(false)
            setIsEditMode(false)

            if (page !== "add") {
                setShowAddForm(false)
            }

            if (detailID) {
                const options = {
                    headers: {
                        "input-parameters": { merchantID: parseInt(detailID) },
                    },
                }
                dispatch(getMerchantTimings(options, history))
            }
        }
    }, [timingCreationSuccess, isSubmitting, page, detailID, dispatch, history])

    // Track length changes for tab completion
    useEffect(() => {
        if (merchantTimings.length > previousTimingsLength) {
            setPreviousTimingsLength(merchantTimings.length)
            if (onTabCompletion) {
                onTabCompletion("4")
            }
        }
    }, [merchantTimings.length, previousTimingsLength, onTabCompletion])

    useEffect(() => {
        if (onFormRef) {
            onFormRef({
                submit: validation.submitForm,
                isValid: validation.isValid,
                values: validation.values,
            })
        }
    }, [onFormRef, validation])

    const handleAddNewTiming = () => {
        dispatch(clearTimingForm())
        resetFormState()
        setEditingTimingIndex(null)
        setIsEditMode(false)
        setShowAddForm(true)
    }

    const handleEditExistingTimings = () => {
        // Clear form 
        dispatch(clearTimingForm())

        merchantTimings.forEach(timing => {
            const detectedStates = detectCheckboxStates(timing.startTime, timing.endTime)
            dispatch(addTimingToForm({
                weekday: timing.weekday,
                startTime: timing.startTime,
                endTime: timing.endTime,
                isFullDay: detectedStates.isFullDay,
                isMidnight: detectedStates.isMidnight,
            }))
        })

        setIsEditMode(true)
        setShowAddForm(true)
        resetFormState()
        setEditingTimingIndex(null)
    }

    const handleCancelForm = () => {
        dispatch(clearTimingForm())
        resetFormState()
        setEditingTimingIndex(null)
        setIsEditMode(false)
        if (page !== "add") {
            setShowAddForm(false)
        }
    }

    // Handle input changes
    const handleInputChange = (field, value) => {
        // For weekday field, handle both array (multi-select) and string (single-select)
        if (field === 'weekday') {
            if (editingTimingIndex !== null) {
                // Edit mode - single select, store as string
                setCurrentTiming(prev => ({
                    ...prev,
                    [field]: value
                }))
            } else {
                // Add mode - multi select, value is already an array from SelectInput
                setCurrentTiming(prev => ({
                    ...prev,
                    [field]: value
                }))
            }
        } else {
            setCurrentTiming(prev => ({
                ...prev,
                [field]: value
            }))
        }
        validation.setFieldValue(field, value)
    }

    // Handle time input changes with auto-detection - FIXED VERSION
    const handleTimeChange = (field, value) => {
        // Update the timing state first
        const newTiming = {
            ...currentTiming,
            [field]: value
        }

        // Get current start and end times
        const currentStartTime = field === 'startTime' ? value : currentTiming.startTime
        const currentEndTime = field === 'endTime' ? value : currentTiming.endTime

        // Auto-detect checkbox states based on current values
        const detectedStates = detectCheckboxStates(currentStartTime, currentEndTime)

        // Update currentTiming state
        setCurrentTiming(prev => ({
            ...prev,
            [field]: value,
            isFullDay: detectedStates.isFullDay,
            isMidnight: detectedStates.isMidnight,
        }))

        // Update formik values
        validation.setFieldValue(field, value)
        validation.setFieldValue('isFullDay', detectedStates.isFullDay)
        validation.setFieldValue('isMidnight', detectedStates.isMidnight)
    }

    // Handle Full Day checkbox
    const handleFullDayChange = (checked) => {
        if (checked) {
            setCurrentTiming(prev => ({
                ...prev,
                isFullDay: true,
                isMidnight: true,
                startTime: "00:00:00",
                endTime: "23:59:59",
            }))
            validation.setValues({
                ...validation.values,
                isFullDay: true,
                isMidnight: true,
                startTime: '00:00:00',
                endTime: '23:59:59'
            })
        } else {
            setCurrentTiming(prev => ({
                ...prev,
                isFullDay: false,
                isMidnight: false,
                startTime: "",
                endTime: "",
            }))
            validation.setFieldValue('isFullDay', false)
            validation.setValues({
                ...validation.values,
                isFullDay: false,
                isMidnight: false,
                startTime: '',
                endTime: ''
            })
        }
    }


    // Handle Midnight checkbox
    const handleMidnightChange = (checked) => {
        // Checked true midnight case
        if (checked) {
            setCurrentTiming(prev => ({
                ...prev,
                isMidnight: true,
                endTime: "23:59:59",
            }))
            validation.setValues({
                ...validation.values,
                isMidnight: true,
                endTime: '23:59:59'
            })
        } else {
            setCurrentTiming(prev => ({
                ...prev,
                isMidnight: false,
                endTime: ''
            }))
            validation.setValues({
                ...validation.values,
                isMidnight: false,
                endTime: ''
            })
        }
    }

    const getValidationForReadOnly = () => ({
        errors: {},
        touched: {},
        values: {},
        setFieldValue: () => { },
        handleBlur: () => { },
        isValid: true,
    })

    const getHeaderButton = () => {
        if (page === "add" || showAddForm) {
            return null
        }

        if (merchantTimings.length === 0) {
            return (
                <AccessControlWrapper
                    contextUri={url.MANAGE_MERCHANT_TIMING}
                    actionType="post"
                >
                    <CButton.Success
                        icon="bx bx-plus"
                        text="Add Timings"
                        onClick={handleAddNewTiming}
                    />
                </AccessControlWrapper>
            )
        } else {
            return (
                <AccessControlWrapper
                    contextUri={url.MANAGE_MERCHANT_TIMING}
                    actionType="post"
                >
                    <CButton.Primary
                        text="Edit Timings"
                        onClick={handleEditExistingTimings}
                    />
                </AccessControlWrapper>
            )
        }
    }

    return (
        <React.Fragment>
            <Row>
                <Col md="12">
                    {timingError && shouldDisplayError(timingError) && (
                        <Alert color="danger" className="mb-3">
                            {timingError.message || timingError.description || "An error occurred"}
                        </Alert>
                    )}

                    {/* Add/Edit Form */}
                    {showAddForm && (
                        <Card className="mb-4">
                            <CardBody>
                                <h6 className="mb-3">
                                    {isEditMode ? "Edit Timings" : (timingFormData.length > 0 ? "Manage Timings" : "Add Timings")}
                                </h6>

                                <Form
                                    className="needs-validation"
                                    onSubmit={e => {
                                        e.preventDefault()
                                        validation.handleSubmit()
                                        return false
                                    }}
                                >
                                    {timingFormData.map((timing, index) => (
                                        <div key={index}>
                                            <Row className="align-items-end">
                                                <Col md="3">
                                                    {editingTimingIndex === index ? (
                                                        <SelectInput
                                                            name={`weekday_${index}`}
                                                            label="Weekday"
                                                            placeholder="Select weekday"
                                                            options={weekdayOptions}
                                                            value={currentTiming.weekday ? weekdayOptions.find(opt => opt.value === currentTiming.weekday) : null}
                                                            readOnly={isSubmitting}
                                                            required={true}
                                                            onSelectionChange={(selectedOption) => {
                                                                handleInputChange('weekday', selectedOption ? selectedOption.value : "")
                                                            }}
                                                            validation={validation}
                                                        />
                                                    ) : (
                                                        <TextInput
                                                            name={`weekday_${index}`}
                                                            label="Weekday"
                                                            value={getWeekdayLabel(timing.weekday)}
                                                            readOnly={true}
                                                            validation={getValidationForReadOnly()}
                                                            inputClassName="text-dark"
                                                        />
                                                    )}
                                                </Col>
                                                <Col md="3">
                                                    <TextInput
                                                        name={`startTime_${index}`}
                                                        label="Start Time"
                                                        placeholder="09:00"
                                                        value={editingTimingIndex === index ? formatTimeForDisplay(currentTiming.startTime) : timing.startTime}
                                                        readOnly={editingTimingIndex !== index || isSubmitting}
                                                        required={editingTimingIndex === index}
                                                        onChange={(e) => {
                                                            if (editingTimingIndex === index) {
                                                                handleTimeChange('startTime', e.target.value)
                                                            }
                                                        }}
                                                        validation={editingTimingIndex === index ? validation : getValidationForReadOnly()}
                                                        inputClassName={editingTimingIndex !== index ? "text-dark" : ""}
                                                    />
                                                </Col>
                                                <Col md="3">
                                                    <TextInput
                                                        name={`endTime_${index}`}
                                                        label="End Time"
                                                        placeholder="17:00"
                                                        value={editingTimingIndex === index ? formatTimeForDisplay(currentTiming.endTime) : timing.endTime}
                                                        readOnly={editingTimingIndex !== index || isSubmitting}
                                                        required={editingTimingIndex === index}
                                                        onChange={(e) => {
                                                            if (editingTimingIndex === index) {
                                                                handleTimeChange('endTime', e.target.value)
                                                            }
                                                        }}
                                                        validation={editingTimingIndex === index ? validation : getValidationForReadOnly()}
                                                        inputClassName={editingTimingIndex !== index ? "text-dark" : ""}
                                                    />
                                                </Col>
                                                <Col md="3" className="d-flex align-items-end">
                                                    <FormGroup className="d-flex gap-2 mb-2">
                                                        {editingTimingIndex === index ? (
                                                            <>
                                                                <IconButton.Save
                                                                    onClick={handleAddTimingToForm}
                                                                    title="Update timing"
                                                                    disabled={isSubmitting || !validation.isValid || !currentTiming.weekday || !currentTiming.startTime || !currentTiming.endTime}
                                                                />
                                                                <IconButton.Cancel
                                                                    onClick={handleCancelEdit}
                                                                    title="Cancel edit"
                                                                    disabled={isSubmitting}
                                                                />
                                                            </>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-primary btn-sm"
                                                                    onClick={() => handleEditTiming(timing, index)}
                                                                    title="Edit timing"
                                                                    disabled={isSubmitting}
                                                                >
                                                                    <i className="bx bx-edit"></i>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-danger btn-sm"
                                                                    onClick={() => handleDeleteTimingFromForm(index)}
                                                                    title="Delete timing"
                                                                    disabled={isSubmitting}
                                                                >
                                                                    <i className="bx bx-trash"></i>
                                                                </button>
                                                            </>
                                                        )}
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            {editingTimingIndex === index && (
                                                <Row className="mb-3">
                                                    <Col md="3">
                                                        <CheckboxInput
                                                            name={`isFullDay_${index}`}
                                                            checkboxLabel="Full Day"
                                                            validation={{
                                                                ...validation,
                                                                values: { ...validation.values, [`isFullDay_${index}`]: currentTiming.isFullDay },
                                                                setFieldValue: (field, value) => {
                                                                    if (field === `isFullDay_${index}`) {
                                                                        handleFullDayChange(value)
                                                                    }
                                                                }
                                                            }}
                                                            readOnly={isSubmitting}
                                                            className="mb-0"
                                                        />
                                                    </Col>
                                                    <Col md="3">
                                                        {/* Empty space for start time */}
                                                    </Col>
                                                    <Col md="4">
                                                        <CheckboxInput
                                                            name={`isMidnight_${index}`}
                                                            checkboxLabel="Midnight"
                                                            validation={{
                                                                ...validation,
                                                                values: { ...validation.values, [`isMidnight_${index}`]: currentTiming.isMidnight },
                                                                setFieldValue: (field, value) => {
                                                                    if (field === `isMidnight_${index}`) {
                                                                        handleMidnightChange(value)
                                                                    }
                                                                }
                                                            }}
                                                            readOnly={isSubmitting}
                                                            className="mb-0"
                                                        />
                                                    </Col>
                                                    <Col md="1">
                                                        {/* Empty space for actions */}
                                                    </Col>
                                                </Row>
                                            )}
                                        </div>
                                    ))}

                                    {editingTimingIndex === null && (
                                        <div>
                                            <Row className="align-items-end">
                                                <Col md="3">
                                                    <SelectInput
                                                        name="weekday"
                                                        label="Weekday"
                                                        placeholder="Select weekdays"
                                                        options={weekdayOptions}
                                                        value={Array.isArray(currentTiming.weekday) ? currentTiming.weekday : []}
                                                        readOnly={isSubmitting}
                                                        required={true}
                                                        isMulti={true}
                                                        onSelectionChange={(selectedOptions) => {
                                                            handleInputChange('weekday', selectedOptions || [])
                                                            validation.setFieldValue('weekday', selectedOptions || [])
                                                        }}
                                                        validation={validation}
                                                        inputStyle={{ height: '38px' }}
                                                    />
                                                </Col>
                                                <Col md="4">
                                                    <TextInput
                                                        name="startTime"
                                                        label="Start Time"
                                                        placeholder="09:00"
                                                        value={formatTimeForDisplay(currentTiming.startTime)}
                                                        readOnly={isSubmitting}
                                                        required={true}
                                                        onChange={(e) => handleTimeChange('startTime', e.target.value)}
                                                        validation={validation}
                                                    />
                                                </Col>
                                                <Col md="4">
                                                    <TextInput
                                                        name="endTime"
                                                        label="End Time"
                                                        placeholder="17:00"
                                                        value={formatTimeForDisplay(currentTiming.endTime)}
                                                        readOnly={isSubmitting}
                                                        required={true}
                                                        onChange={(e) => handleTimeChange('endTime', e.target.value)}
                                                        validation={validation}
                                                    />
                                                </Col>
                                                <Col md="1" className="d-flex align-items-end">
                                                    <FormGroup className="d-flex gap-2 mb-2">
                                                        <IconButton.Add
                                                            onClick={handleAddTimingToForm}
                                                            disabled={isSubmitting || !validation.isValid || (Array.isArray(currentTiming.weekday) && currentTiming.weekday.length === 0) || !currentTiming.startTime || !currentTiming.endTime}
                                                            title="Add timing"
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col md="3">
                                                    <CheckboxInput
                                                        name="isFullDay"
                                                        checkboxLabel="Full Day"
                                                        validation={{
                                                            ...validation,
                                                            setFieldValue: (field, value) => {
                                                                if (field === 'isFullDay') {
                                                                    handleFullDayChange(value)
                                                                }
                                                            }
                                                        }}
                                                        readOnly={isSubmitting}
                                                    />
                                                </Col>
                                                <Col md="4">
                                                    {/* Empty space for start time */}
                                                </Col>
                                                <Col className="mb-3" md="4">
                                                    <CheckboxInput
                                                        name="isMidnight"
                                                        checkboxLabel="Midnight"
                                                        validation={{
                                                            ...validation,
                                                            setFieldValue: (field, value) => {
                                                                if (field === 'isMidnight') {
                                                                    handleMidnightChange(value)
                                                                }
                                                            }
                                                        }}
                                                        readOnly={isSubmitting}
                                                        className="mb-0"
                                                    />
                                                </Col>
                                                <Col md="1">
                                                    {/* Empty space for actions */}
                                                </Col>
                                            </Row>
                                        </div>
                                    )}
                                </Form>

                                {/* Form Action Buttons */}
                                <div className="text-end">
                                    {page === "detail" && (
                                        <CButton.Secondary
                                            text="Cancel"
                                            className="me-2"
                                            onClick={handleCancelForm}
                                            disabled={isSubmitting}
                                        />
                                    )}

                                    {(timingFormData.length > 0 || isEditMode) && (
                                        <AccessControlWrapper
                                            contextUri={url.MANAGE_MERCHANT_TIMING}
                                            actionType="post"
                                        >
                                            <ConfirmButton
                                                contextUri={url.MANAGE_MERCHANT_TIMING}
                                                actionType="post"
                                                validateBeforeConfirm={() =>
                                                    Promise.resolve(isEditMode || timingFormData.length > 0)
                                                }
                                                onConfirm={handleFinalSubmit}
                                                message={`Are you sure you want to ${isEditMode ? 'update' : 'save'} these timings?`}
                                                disabled={isSubmitting}
                                                renderButton={
                                                    <CButton.Primary
                                                        text={isEditMode ? "Update Timings" : "Save Timings"}
                                                        loading={isSubmitting}
                                                        disabled={isSubmitting}
                                                    />
                                                }
                                            />
                                        </AccessControlWrapper>
                                    )}
                                </div>
                            </CardBody>
                        </Card>
                    )}

                    {/* Timings Table - Only show when not in edit mode */}
                    {!isEditMode && (
                        <SimpleTableContainer
                            title="Timings"
                            columns={[
                                {
                                    key: "weekday",
                                    label: "Weekday",
                                },
                                {
                                    key: "startTime",
                                    label: "Start Time",
                                },
                                {
                                    key: "endTime",
                                    label: "End Time",
                                },
                            ]}
                            data={merchantTimings.map((timing, index) => ({
                                ...timing,
                                id: index,
                                weekday: getWeekdayLabel(timing.weekday),
                                startTime: timing.startTime,
                                endTime: timing.endTime,
                            }))}
                            loading={isLoadingTimings}
                            emptyMessage="No timings defined yet."
                            emptyIcon="bx bx-time"
                            showActions={false}
                            headerActions={getHeaderButton()}
                        />
                    )}
                </Col>
            </Row>
        </React.Fragment>
    )
}